import { useEffect, useState } from 'react';
import { OrderStatus } from '../../types/order';
import type { OrderFormData } from '../CreateOrderWizard';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PhoneInput } from '../ui/phone-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

type CustomerInfoStepProps = {
  data: OrderFormData;
  onChange: (data: OrderFormData) => void;
};

type AddressFormData = {
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  country_code: string;
};

const CustomerInfoStep = ({ data, onChange }: CustomerInfoStepProps) => {
  const [addresses, setAddresses] = useState<any[]>([]);
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [billingAddress, setBillingAddress] = useState<AddressFormData>({
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country_code: 'GB',
  });
  const [deliveryAddress, setDeliveryAddress] = useState<AddressFormData>({
    address_line1: '',
    address_line2: '',
    city: '',
    region: '',
    postal_code: '',
    country_code: 'GB',
  });

  useEffect(() => {
    loadAddresses();
  }, []);

  const loadAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/addresses', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      setAddresses(result.data || []);
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const handleChange = (updates: Partial<OrderFormData>) => {
    onChange({ ...data, ...updates });
  };

  // Sync address forms with parent when they change
  useEffect(() => {
    if (showBillingForm && billingAddress.address_line1) {
      handleChange({ billing_address: billingAddress, billing_address_id: '' });
    }
  }, [billingAddress]);

  useEffect(() => {
    if (showDeliveryForm && deliveryAddress.address_line1) {
      handleChange({ delivery_address: deliveryAddress, delivery_address_id: '' });
    }
  }, [deliveryAddress]);

  // Sync delivery address with billing when "Same as billing" is checked
  useEffect(() => {
    if (sameAsBilling) {
      // Copy billing address to delivery
      if (showBillingForm) {
        // If using new billing address form
        setDeliveryAddress(billingAddress);
        handleChange({
          delivery_address: billingAddress,
          delivery_address_id: ''
        });
      } else {
        // If using existing billing address
        handleChange({
          delivery_address_id: data.billing_address_id,
          delivery_address: undefined
        });
      }
    }
  }, [sameAsBilling, billingAddress, data.billing_address_id, showBillingForm]);

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="customer_name">Customer Name *</Label>
        <Input
          id="customer_name"
          value={data.customer_name}
          onChange={(e) => handleChange({ customer_name: e.target.value })}
          placeholder="Enter customer name"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="customer_email">Email *</Label>
          <Input
            id="customer_email"
            type="email"
            value={data.customer_email || ''}
            onChange={(e) => handleChange({ customer_email: e.target.value })}
            placeholder="customer@example.com"
          />
        </div>

        <div>
          <Label htmlFor="customer_phone">Phone Number *</Label>
          <PhoneInput
            id="customer_phone"
            value={data.customer_phone || ''}
            onChange={(value) => handleChange({ customer_phone: value as string })}
            defaultCountry="GB"
            placeholder="Enter phone number"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="order_date">Order Date</Label>
          <Input
            id="order_date"
            type="date"
            value={data.order_date}
            onChange={(e) => handleChange({ order_date: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="required_by_date">Required By</Label>
          <Input
            id="required_by_date"
            type="date"
            value={data.required_by_date}
            onChange={(e) => handleChange({ required_by_date: e.target.value })}
          />
        </div>
      </div>

      {/* Billing Address */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Billing Address</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowBillingForm(!showBillingForm)}
          >
            {showBillingForm ? 'Select Existing' : 'Enter New Address'}
          </Button>
        </div>

        {!showBillingForm ? (
          <Select
            value={data.billing_address_id}
            onValueChange={(value) => handleChange({ billing_address_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select billing address" />
            </SelectTrigger>
            <SelectContent>
              {addresses.map((addr) => (
                <SelectItem key={addr.id} value={addr.id}>
                  {addr.formatted_address || `${addr.address_line1}, ${addr.city}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="p-3 border rounded-lg bg-gray-50 space-y-3">
            <div>
              <Label htmlFor="billing_line1">Address Line 1 *</Label>
              <Input
                id="billing_line1"
                value={billingAddress.address_line1}
                onChange={(e) => setBillingAddress({ ...billingAddress, address_line1: e.target.value })}
                placeholder="Street address"
              />
            </div>
            <div>
              <Label htmlFor="billing_line2">Address Line 2</Label>
              <Input
                id="billing_line2"
                value={billingAddress.address_line2}
                onChange={(e) => setBillingAddress({ ...billingAddress, address_line2: e.target.value })}
                placeholder="Apartment, suite, etc. (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="billing_city">City/Town *</Label>
                <Input
                  id="billing_city"
                  value={billingAddress.city}
                  onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div>
                <Label htmlFor="billing_region">County</Label>
                <Input
                  id="billing_region"
                  value={billingAddress.region}
                  onChange={(e) => setBillingAddress({ ...billingAddress, region: e.target.value })}
                  placeholder="County (optional)"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="billing_postcode">Postcode *</Label>
                <Input
                  id="billing_postcode"
                  value={billingAddress.postal_code}
                  onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value.toUpperCase() })}
                  placeholder="SW1A 1AA"
                />
              </div>
              <div>
                <Label htmlFor="billing_country">Country</Label>
                <Select
                  value={billingAddress.country_code}
                  onValueChange={(value) => setBillingAddress({ ...billingAddress, country_code: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="IE">Ireland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Same as Billing Address Checkbox */}
      <div className="flex items-center space-x-2 py-2">
        <Checkbox
          id="same-as-billing"
          checked={sameAsBilling}
          onCheckedChange={(checked) => setSameAsBilling(checked as boolean)}
        />
        <label
          htmlFor="same-as-billing"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          Delivery address is the same as billing address
        </label>
      </div>

      {/* Delivery Address */}
      {!sameAsBilling && (
        <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Delivery Address</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowDeliveryForm(!showDeliveryForm)}
          >
            {showDeliveryForm ? 'Select Existing' : 'Enter New Address'}
          </Button>
        </div>

        {!showDeliveryForm ? (
          <Select
            value={data.delivery_address_id}
            onValueChange={(value) => handleChange({ delivery_address_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select delivery address" />
            </SelectTrigger>
            <SelectContent>
              {addresses.map((addr) => (
                <SelectItem key={addr.id} value={addr.id}>
                  {addr.formatted_address || `${addr.address_line1}, ${addr.city}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div className="p-3 border rounded-lg bg-gray-50 space-y-3">
            <div>
              <Label htmlFor="delivery_line1">Address Line 1 *</Label>
              <Input
                id="delivery_line1"
                value={deliveryAddress.address_line1}
                onChange={(e) => setDeliveryAddress({ ...deliveryAddress, address_line1: e.target.value })}
                placeholder="Street address"
              />
            </div>
            <div>
              <Label htmlFor="delivery_line2">Address Line 2</Label>
              <Input
                id="delivery_line2"
                value={deliveryAddress.address_line2}
                onChange={(e) => setDeliveryAddress({ ...deliveryAddress, address_line2: e.target.value })}
                placeholder="Apartment, suite, etc. (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="delivery_city">City/Town *</Label>
                <Input
                  id="delivery_city"
                  value={deliveryAddress.city}
                  onChange={(e) => setDeliveryAddress({ ...deliveryAddress, city: e.target.value })}
                  placeholder="City"
                />
              </div>
              <div>
                <Label htmlFor="delivery_region">County</Label>
                <Input
                  id="delivery_region"
                  value={deliveryAddress.region}
                  onChange={(e) => setDeliveryAddress({ ...deliveryAddress, region: e.target.value })}
                  placeholder="County (optional)"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="delivery_postcode">Postcode *</Label>
                <Input
                  id="delivery_postcode"
                  value={deliveryAddress.postal_code}
                  onChange={(e) => setDeliveryAddress({ ...deliveryAddress, postal_code: e.target.value.toUpperCase() })}
                  placeholder="SW1A 1AA"
                />
              </div>
              <div>
                <Label htmlFor="delivery_country">Country</Label>
                <Select
                  value={deliveryAddress.country_code}
                  onValueChange={(value) => setDeliveryAddress({ ...deliveryAddress, country_code: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GB">United Kingdom</SelectItem>
                    <SelectItem value="IE">Ireland</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
        </div>
      )}

      <div>
        <Label htmlFor="special_instructions">Special Instructions</Label>
        <Textarea
          id="special_instructions"
          value={data.special_instructions}
          onChange={(e) => handleChange({ special_instructions: e.target.value })}
          placeholder="Any special requirements or notes"
          rows={2}
        />
      </div>

      <div>
        <Label htmlFor="status">Status</Label>
        <Select
          value={data.status}
          onValueChange={(value) => handleChange({ status: value as OrderStatus })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={OrderStatus.DRAFT}>Draft</SelectItem>
            <SelectItem value={OrderStatus.PENDING}>Pending</SelectItem>
            <SelectItem value={OrderStatus.CONFIRMED}>Confirmed</SelectItem>
            <SelectItem value={OrderStatus.IN_PROGRESS}>In Progress</SelectItem>
            <SelectItem value={OrderStatus.COMPLETED}>Completed</SelectItem>
            <SelectItem value={OrderStatus.ON_HOLD}>On Hold</SelectItem>
            <SelectItem value={OrderStatus.CANCELLED}>Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default CustomerInfoStep;
